import { useLang } from 'hooks/lang';

import { loaders, actions } from 'entities';

import {
  Table,
  Column,
  Row,
  Modal,
  Button,
  Card,
  LabeledInput,
  IconButton,
} from 'components/Blocks';

import { PlusIcon, OutsourceIcon, SaveIcon, PencilIcon } from 'components/Icons';

import { useFormControl } from 'hooks';
import { listPartialUpdate, sortByName } from 'utils/array';

interface State {
  name: string;
  open: boolean;
  id?: string | null;
}

function OutsourceProviders() {
  const { t } = useLang();

  const { state, dispatch, handleChange, resetDefault } = useFormControl<State>({
    name: '',
    open: false,
  });

  const providers = loaders.outsourceProviders.listOutsourceProviders.use({
    defaultValue: [],
  });

  const createProvider = actions.outsourceProviders.createOutsourceProvider.use({
    block: !state.name,

    params: {
      name: state.name,
    },

    onSuccess: (provider) => {
      providers.overwrite((old) => sortByName([...old, provider]));

      resetDefault();
    },
  });

  const updateProvider = actions.outsourceProviders.updateOutsourceProvider.use({
    block: !state.id,

    params: {
      name: state.name,
      id: state.id!,
    },

    onSuccess: (_, { name, id }) => {
      providers.overwrite((old) => sortByName(listPartialUpdate(old, { name, id })));

      resetDefault();
    },
  });

  const action = state.id ? updateProvider : createProvider;

  return (
    <>
      <Modal
        isOpen={state.open}
        title={`${state.id ? t('edit') : t('create')} ${t('provider')}`}
        close={() => dispatch({ open: false, id: null, name: '' })}
        width="min(90vw, 480px)"
      >
        <LabeledInput name="name" value={state.name} onChange={handleChange} label={t('name')} />

        <Button disabled={!state.name.trim()} {...action.buttonProps}>
          <SaveIcon />
          {t('save')}
        </Button>
      </Modal>

      <Column gap="8">
        <Row justifyContent="space-between">
          <h4 className="flex items-center gap-2">
            <OutsourceIcon size={28} />

            {t('outsource-providers')}
          </h4>

          <Button format="square" round size="small" onClick={() => dispatch({ open: true })}>
            <PlusIcon />
          </Button>
        </Row>

        <Card>
          <Table
            loading={providers.errorOrLoading}
            items={providers.data}
            uniqueKey="id"
            spacing="medium"
            columns={[
              { label: t('name') as string, property: 'name', search: true },
              {
                render: (provider) => (
                  <IconButton
                    className="ml-auto flex"
                    onClick={() => dispatch({ ...provider, open: true })}
                  >
                    <PencilIcon size={14} />
                  </IconButton>
                ),
              },
            ]}
          />
        </Card>
      </Column>
    </>
  );
}

export default OutsourceProviders;
